import { BrandContentDto } from "@growth/dto";
import { Pillar } from "@growth/schema";
import { Button, Field, Input, Textarea } from "@growth/ui";
import { LoaderCircle, Plus, Save, Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ChannelsEditor } from "../components/channels-editor";
import { ErrorText, PageHeader } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { useContentSettings, useSaveContentSettings } from "../lib/content";

/**
 * How content is shaped for this brand: the writer reads this on every run, next to Knowledge.
 * Nothing here is built into the code, so any business can set it up.
 */
export const ContentSetupPage = () => {
  const { brand } = useActive();
  const settings = useContentSettings();
  return (
    <section className="max-w-3xl space-y-6">
      <PageHeader eyebrow={brand?.name ?? ""} title="Content setup">
        Who posts, where, in what style, and the recurring formats (pillars) pieces follow.
      </PageHeader>
      {settings.isError && <ErrorText>{apiErrorMessage(settings.error)}</ErrorText>}
      {settings.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {settings.data && <SetupForm initial={settings.data} />}
    </section>
  );
};

const SetupForm = ({ initial }: { initial: BrandContentDto }) => {
  const { atLeast } = useActive();
  const canEdit = atLeast("EDITOR");
  const save = useSaveContentSettings();
  const [form, setForm] = useState(initial);
  const set = <K extends keyof BrandContentDto>(key: K, value: BrandContentDto[K]) =>
    setForm({ ...form, [key]: value });
  const setPillar = (index: number, patch: Partial<Pillar>) =>
    set(
      "pillars",
      form.pillars.map((pillar, i) => (i === index ? { ...pillar, ...patch } : pillar)),
    );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(form);
  };

  const text = (
    key: "language" | "linkedinAuthor" | "communities" | "hashtags",
    label: string,
    hint: string,
    placeholder: string,
  ) => (
    <Field label={label} hint={hint}>
      {(props) => (
        <Input
          {...props}
          disabled={!canEdit}
          placeholder={placeholder}
          value={form[key]}
          onChange={(event) => set(key, event.target.value)}
        />
      )}
    </Field>
  );

  return (
    <form onSubmit={submit} className="space-y-6">
      <div className="space-y-4 rounded-xl border border-line bg-white p-5">
        <h2 className="font-serif text-lg text-ink">Voice and channels</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {text("language", "Language", "Spelling and register", "Australian English")}
          {text(
            "linkedinAuthor",
            "LinkedIn posts are written as",
            "Who's posting, in first person",
            "Sam, Northwind's founder",
          )}
          {text(
            "communities",
            "Reddit communities",
            "Where answers will be posted",
            "r/smallbusiness",
          )}
          {text("hashtags", "Example hashtags", "For captions", "#smallbusiness #invoicing")}
        </div>
        <Field label="Keep examples varied" hint="What to rotate so a batch doesn't repeat itself">
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              disabled={!canEdit}
              value={form.exampleVariety}
              onChange={(event) => set("exampleVariety", event.target.value)}
            />
          )}
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={form.pricingPublic}
            onChange={(event) => set("pricingPublic", event.target.checked)}
          />
          Pricing is public (when off, drafts that mention prices are blocked)
        </label>
      </div>

      <div className="space-y-3 rounded-xl border border-line bg-white p-5">
        <div>
          <h2 className="font-serif text-lg text-ink">Channels</h2>
          <p className="text-sm text-muted">
            Where this brand posts, in which formats and how often. The director plans each week to
            these numbers, and each channel's owner sees its posts on their Today list.
          </p>
        </div>
        <ChannelsEditor
          value={form.channels}
          onChange={(channels) => set("channels", channels)}
          disabled={!canEdit}
        />
      </div>

      <div className="space-y-4 rounded-xl border border-line bg-white p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-lg text-ink">Pillars</h2>
          {canEdit && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => set("pillars", [...form.pillars, { id: "", name: "", format: "" }])}
            >
              <Plus className="size-4" /> Add pillar
            </Button>
          )}
        </div>
        {form.pillars.length === 0 && (
          <p className="text-sm text-muted">No pillars yet: pieces use a general format.</p>
        )}
        {form.pillars.map((pillar, index) => (
          <div
            key={index}
            className="grid gap-3 border-t border-line pt-4 sm:grid-cols-[10rem_minmax(0,1fr)_auto]"
          >
            <Field label="Id">
              {(props) => (
                <Input
                  {...props}
                  required
                  disabled={!canEdit}
                  placeholder="teardown"
                  value={pillar.id}
                  onChange={(event) => setPillar(index, { id: event.target.value })}
                />
              )}
            </Field>
            <Field label="Name">
              {(props) => (
                <Input
                  {...props}
                  required
                  disabled={!canEdit}
                  placeholder="Teardown"
                  value={pillar.name}
                  onChange={(event) => setPillar(index, { name: event.target.value })}
                />
              )}
            </Field>
            {canEdit && (
              <Button
                type="button"
                variant="ghost"
                className="self-end"
                aria-label={`Remove ${pillar.name || "pillar"}`}
                onClick={() =>
                  set(
                    "pillars",
                    form.pillars.filter((_, i) => i !== index),
                  )
                }
              >
                <Trash2 className="size-4" />
              </Button>
            )}
            <Field label="Format: how pieces in this pillar are shaped" className="sm:col-span-3">
              {(props) => (
                <Textarea
                  {...props}
                  required
                  minLength={10}
                  rows={2}
                  disabled={!canEdit}
                  value={pillar.format}
                  onChange={(event) => setPillar(index, { format: event.target.value })}
                />
              )}
            </Field>
          </div>
        ))}
      </div>

      {save.isError && <ErrorText>{apiErrorMessage(save.error)}</ErrorText>}
      {canEdit && (
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <Save className="size-4" />
          )}
          Save
        </Button>
      )}
    </form>
  );
};
